import React, { Component } from 'react'
import { connect } from "react-redux"
import trans from "trans"
import { debounce, find } from 'lodash';
import { echo } from "app/components/websocketeer";
import { getExistingSimilarityResult, godProjectId, isGodModeOn, projectId } from "app/helpers/api";
import { URL } from "app/config/api-config"

import {
    css,
    FieldLabel,
    Grid,
    GridCell,
    InputGroup,
    Range,
    Spinner,
    Theme,
    Toggler,
    Progress,
    Icon,
    Message,
    PopupHint,
} from "@bdfx/mergado-ui-kit"

import { Query, Template, SettingsFormType } from 'app/types/model-types';
import { ID, ReduxList, intboolean } from 'app/types/app-types';
import { createSimilarityExport, ExportsType } from 'app/reducers/exports';
import { iconDisabledColor } from './tools-panel';
import { SimilarityWebSocketMessage } from 'products/components/product-table'
import { RandomProduct } from 'shape-edit/types';
import { AxiosResponse } from 'axios';
import { VisualSimilarityType } from 'products/types';
import { Slider } from '@mui/material';
import { saveProjectSettings, getProjectSettings } from 'app/helpers/api';
import { selectFormValues } from 'app/reducers/form';

export const DEFAULT_VISUAL_SIMILARITY_THRESHOLD = .5
export const DEFAULT_VISUAL_SIMILARITY_N_LIMIT = 5

interface Props {
    product: RandomProduct
    templateId: ID
    enabled: intboolean
    n: number
    threshold: number
    templateids: ID[]
    publishAutomatically: boolean
    templates: ReduxList<Template>
    queries: ReduxList<Query>
    exportSimilarity: ExportsType
    updateTemplateFieldValue: Function
    createSimilarityExport: typeof createSimilarityExport
    settingsForm: SettingsFormType
}

interface State {
    isWaitingForTimeout: boolean
    statusBarMessage: string
    similarImages: string
    nLimit: number
    threshold: number
    imageHash: string
}

class VisualSimilarity extends Component<Props, State> {
    constructor(props) {
        super(props)

        this.state = {
            isWaitingForTimeout: false,
            statusBarMessage: props.templateids && props.templateids.length > 0 ? null : 'please_select_templates',
            similarImages: null,
            nLimit: null,
            threshold: null,
            imageHash: null,
        }
    }

    updateResults = () => {
        const { product, n, threshold, templateId, templateids } = this.props

        //get current random product result if exists:
        if (product?.image_hash && templateids) {
            const requestedImageHash = product.image_hash
            const requestedN = n || DEFAULT_VISUAL_SIMILARITY_N_LIMIT
            const requestedThreshold = threshold || DEFAULT_VISUAL_SIMILARITY_THRESHOLD

            this.setState({ statusBarMessage: 'loading_results' })
            getExistingSimilarityResult(requestedImageHash, requestedN, requestedThreshold, templateId, templateids)
                .then((response: AxiosResponse<VisualSimilarityType>) => {
                    console.debug(`looking for results for image hash: ${requestedImageHash}, n: ${requestedN}, threshold: ${requestedThreshold}, template: ${templateId}, templateids: ${templateids}`)
                    if (response.data?.results) {
                        console.debug('found results')
                        this.setState({
                            statusBarMessage: 'done',
                            similarImages: response.data.results,
                            nLimit: requestedN,
                            threshold: requestedThreshold,
                            imageHash: requestedImageHash,
                        })
                    } else {
                        console.debug('no results, export to csv', product)
                        this.exportToCSVDebounced(templateId, templateids, requestedN, requestedThreshold)
                    }
                })
                .catch((e) => console.error('error', e))
        } else if (!product?.image_hash) {
            console.debug('product not loaded', product)
            this.setState({ statusBarMessage: 'waiting_for_product_to_be_loaded' })
        }
    }

    componentDidMount(): void {
        const actualProjectId = isGodModeOn ? godProjectId : projectId

        echo.channel(`private-project.${actualProjectId}`)
            .listen('.similarity.finished', (event) => this.handleProcessDone(event))

        echo.channel(`private-project.${actualProjectId}`)
            .listen('.similarity.started', (event) => this.handleProcessStarted(event))

        this.updateResults()
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps) {
            if (
                (!prevProps.product && this.props.product) ||
                (prevProps.product?.id !== this.props.product.id) ||
                (prevProps.product?.image_hash !== this.props.product.image_hash)
            ) {
                this.updateResults()
            } else if (this.state.statusBarMessage === 'exporting' && this.props.exportSimilarity.isExported) {
                this.setState({ statusBarMessage: 'exported' })
            }
        }
    }

    saveProjectSettings = (settingsForm: SettingsFormType) => {
        saveProjectSettings(settingsForm)
            .then((response: AxiosResponse<any>) => {
                console.debug('saved')
            })
            .catch(error => {
                console.error('error', error)
            })
    }

    handleProcessStarted = (message: SimilarityWebSocketMessage) => {
        const { product } = this.props
        if (message.project_id === projectId && product?.id === message.product_id) {
            console.debug('VisualSimilarity: process started', message)
            this.setState({
                imageHash: message.image_hash,
                statusBarMessage: 'task_queued'
            })
        }
    }

    handleProcessDone = (message: SimilarityWebSocketMessage) => {
        const { n, threshold, templateId } = this.props
        const { imageHash } = this.state

        console.debug('VisualSimilarity: process done', { message, n, threshold, templateId, imageHash })

        if (
            `${message.project_id}` === `${projectId}` &&
            `${n}` === `${message.n_limit}` &&
            `${threshold}` === `${message.threshold}` &&
            `${imageHash}` === `${message.image_hash}` &&
            `${templateId}` === `${message.template_id}`
        ) {
            this.setState({
                similarImages: message.results,
                nLimit: message.n_limit,
                threshold: message.threshold,
                statusBarMessage: 'done'
            })
            console.debug('VisualSimilarity: process finished', message)
        }
    }

    exportToCSVDebounced = debounce((templateId: ID, templateIds: ID[], nLimit: number, threshold: number) => {
        console.debug('export to csv debounced', templateId, templateIds, nLimit, threshold, this.props.publishAutomatically)
        if (this.props.publishAutomatically) {
            this.setState({
                isWaitingForTimeout: true,
                similarImages: null,
                nLimit: null,
                threshold: null,
            })
            this.exportToCSVNow(templateId, templateIds, nLimit, threshold)
        }
    }, 3000)

    exportToCSVNow = (templateId: ID, templateIds: ID[], nLimit: number, threshold: number) => {
        const { product } = this.props

        this.setState({ isWaitingForTimeout: false })

        const filteredTemplateIds = templateIds ? templateIds.filter(v => v !== '') : null
        if (filteredTemplateIds && filteredTemplateIds.length > 0) {
            this.setState({ statusBarMessage: 'exporting' })
            this.props.createSimilarityExport(templateId, filteredTemplateIds, product.id, nLimit, threshold, true)
        } else {
            this.setState({ statusBarMessage: 'please_select_templates' })
            console.debug('empty list')
        }
    }

    render() {
        const { queries, templateId, templates, product, exportSimilarity, enabled, n, threshold, templateids, settingsForm } = this.props
        const { isWaitingForTimeout, similarImages, nLimit, statusBarMessage } = this.state

        if (!queries.isLoaded || !queries.data || !templates.isLoaded || !templates.data) {
            return <Spinner type={"bubbles"} />
        }

        const countResults = similarImages ? similarImages.split(";").length - 1 : 0
        const items = templates.data
            .map((t: Template) => {
                const queryNames = Array.isArray(t.queries) ? t.queries.map(queryid => {
                    const query = find(queries.data, (query: Query) => {
                        return `${query.id}` === `${queryid}`
                    })
                    return query?.name === '♥ALLPRODUCTS♥' ? trans('all_products') : query?.name
                }) : []

                return {
                    label: `template_${t.id}`,
                    text: <span>
                        <span className={`template-name ${t.active ? 'active' : 'inactive'}`} title={`${t.id}`}>{t.name}</span>
                        <span className={'sub'}>{queryNames.join(', ')}</span>
                        <span className={'products-count'}>[{t.products_count}]</span>
                    </span>,
                    value: `${t.id}`,
                }
            })

        const noItems = !items || items.length === 0

        const percents = exportSimilarity.isExporting && exportSimilarity.data && exportSimilarity.data.exported_rows && exportSimilarity.data.total_rows ?
            Math.round((exportSimilarity.data.exported_rows / exportSimilarity.data.total_rows) * 100) : 0

        const isExporting = exportSimilarity.isExporting && (!exportSimilarity.data || `${exportSimilarity.data?.template_id}` === `${templateId}`)

        return (
            <div className={`hide-buttons horizontal-fixed-size`}>
                <Toggler
                    value={enabled}
                    onLabel={trans('visual_similarity_enabled')}
                    title={trans('visual_similarity_enabled')}
                    onChange={(newValue) => {
                        this.props.updateTemplateFieldValue(templateId, {
                            visual_similarity_enabled: newValue,
                            visual_similarity_n: newValue ? (n ? n : DEFAULT_VISUAL_SIMILARITY_N_LIMIT) : null,
                            visual_similarity_threshold: newValue ? (threshold ? threshold : DEFAULT_VISUAL_SIMILARITY_THRESHOLD) : null
                        })
                    }}
                />

                {!!enabled &&
                    <div style={{ marginTop: '20px' }}>
                        {statusBarMessage &&
                            <Message>
                            {trans(statusBarMessage)} &nbsp;

                            {(statusBarMessage === 'done' && countResults < nLimit) &&
                                <PopupHint icon={<Icon type={'info-circle'} color={iconDisabledColor} />}>{trans("similarity_less_than_n")}</PopupHint>
                            }
                            </Message>
                        }

                        <FieldLabel style={{ marginTop: '20px' }}>
                            {trans('visual_similarity_search_products_in')}
                            {(isWaitingForTimeout && templateids && templateids.length > 0) &&
                                <CountDown>
                                    <Icon type={'circle'} color={iconDisabledColor} size={18} />
                                </CountDown>
                            }
                        </FieldLabel>

                        {!isExporting ?
                            <React.Fragment>
                                <StyledDiv>
                                    {noItems ? <div className={'no-items'}>
                                        <Icon type={'search-light'} size={16} />
                                        <div style={{
                                            textAlign: 'center',
                                            fontStyle: 'italic',
                                        }}>{trans('no_items_filtered')}</div>
                                    </div> :
                                        <StyledInputGroup
                                            search={true}
                                            value={templateids}
                                            items={items}
                                            placeholder={trans("filter")}
                                            onChange={(value) => {
                                                const filteredValue = value ? value.filter(v => v !== '') : null
                                                this.props.updateTemplateFieldValue(templateId, {
                                                    visual_similarity_templateids: filteredValue,
                                                    visual_similarity_n: (n ? n : DEFAULT_VISUAL_SIMILARITY_N_LIMIT),
                                                    visual_similarity_threshold: (threshold ? threshold : DEFAULT_VISUAL_SIMILARITY_THRESHOLD),
                                                })
                                                this.setState({
                                                    isWaitingForTimeout: true,
                                                    statusBarMessage: filteredValue ? 'waiting_before_publish' : 'please_select_templates',
                                                    similarImages: null,
                                                    nLimit: null,
                                                    threshold: null,
                                                })
                                                this.exportToCSVDebounced(templateId, filteredValue, n, threshold)
                                            }}
                                            orientation='vertical'
                                        />
                                    }
                                </StyledDiv>

                                <FieldLabel>{trans('visual_similarity_n')} <PopupHint icon={<Icon type={'info-circle'} color={iconDisabledColor} />}>{trans("similarity_n_limit_info")}</PopupHint></FieldLabel>
                                <div style={{ margin: '25px 0', padding: '0 20px' }}>
                                    <Slider
                                        min={1}
                                        max={20}
                                        value={n || DEFAULT_VISUAL_SIMILARITY_N_LIMIT}
                                        onChange={(event: Event, value: number) => {
                                            this.setState({ statusBarMessage: templateids && templateids.length > 0 ? 'waiting_before_publish' : 'please_select_templates', })
                                            this.props.updateTemplateFieldValue(templateId, {
                                                visual_similarity_n: value,
                                                visual_similarity_threshold: (threshold ? threshold : DEFAULT_VISUAL_SIMILARITY_THRESHOLD),
                                            })
                                            this.exportToCSVDebounced(templateId, templateids, value, threshold)
                                        }}
                                        marks={[
                                            { value: 1, label: 1 },
                                            { value: 10, label: 10 },
                                            { value: 20, label: 20 },
                                        ]}
                                        valueLabelDisplay="on"
                                        getAriaLabel={() => `${trans('visual_similarity_n')}`}
                                        getAriaValueText={(value) => `${value}`}
                                        valueLabelFormat={(value) => `${value}`}
                                    />
                                </div>

                                <FieldLabel>{trans('visual_similarity_threshold')}<PopupHint icon={<Icon type={'info-circle'} color={iconDisabledColor} />}>{trans("similarity_threshold_info")}</PopupHint></FieldLabel>
                                <div style={{ margin: '25px 0', padding: '0 20px' }}>
                                    <Slider
                                        min={0.05}
                                        max={1.00}
                                        step={.05}
                                        value={threshold || DEFAULT_VISUAL_SIMILARITY_THRESHOLD}
                                        onChange={(event: Event, value: number) => {
                                            this.setState({ statusBarMessage: templateids && templateids.length > 0 ? 'waiting_before_publish' : 'please_select_templates', })
                                            const newThreshold = Math.round(value * 100) / 100
                                            this.props.updateTemplateFieldValue(templateId, {
                                                visual_similarity_n: (n ? n : DEFAULT_VISUAL_SIMILARITY_N_LIMIT),
                                                visual_similarity_threshold: newThreshold,
                                            })
                                            this.exportToCSVDebounced(templateId, templateids, n, newThreshold)
                                        }}
                                        marks={[
                                            { value: 0.05, label: 0.05 },
                                            { value: 0.5, label: 0.5 },
                                            { value: 1.0, label: 1.0 },
                                        ]}
                                        valueLabelDisplay="auto"
                                        getAriaLabel={() => `${trans('visual_similarity_threshold')}`}
                                        getAriaValueText={(value) => `${value}`}
                                        valueLabelFormat={(value) => `${value}`}
                                    />
                                </div>

                                {/* <Range
                                    key={`range_${n}`}
                                    label={trans('visual_similarity_n')}
                                    min={1}
                                    max={20}
                                    value={n || DEFAULT_VISUAL_SIMILARITY_N_LIMIT}
                                    onChange={(e) => {
                                        const newN = parseInt(e)
                                        this.props.updateTemplateFieldValue(templateId, {
                                            visual_similarity_n: newN,
                                            visual_similarity_threshold: (threshold ? threshold : DEFAULT_VISUAL_SIMILARITY_THRESHOLD),
                                        })
                                        this.exportToCSVDebounced(templateId, templateids, newN, threshold)
                                    }}
                                /> */}

                                {/* <Range
                                    key={`threshold_${threshold}`}
                                    label={trans('visual_similarity_threshold')}
                                    min={0.05}
                                    max={1.00}
                                    step={.05}
                                    value={threshold || DEFAULT_VISUAL_SIMILARITY_THRESHOLD}
                                    onChange={(e) => {
                                        const newThreshold = Math.round(e * 100) / 100
                                        this.props.updateTemplateFieldValue(templateId, {
                                            visual_similarity_n: (n ? n : DEFAULT_VISUAL_SIMILARITY_N_LIMIT),
                                            visual_similarity_threshold: newThreshold,
                                        })
                                        this.exportToCSVDebounced(templateId, templateids, n, newThreshold)
                                    }}
                                /> */}

                            </React.Fragment> :
                            <div className={'centering'}>
                                {(percents > 0 && percents < 100) ?
                                    <Progress
                                        showLabel={true}
                                        value={percents}
                                        color="success"
                                        max={100}
                                        getValueLabel={(val) => val + ' % ' + trans('please_wait_generating_export')}
                                    /> :
                                    <Spinner type={'bubbles'} />
                                }
                            </div>
                        }

                        {(product?.id && similarImages && threshold === this.state.threshold && n === this.state.nLimit) &&
                            <SimilarImagesGrid>
                                {similarImages.split("\n").map((row, k) => {
                                    if (!row) {
                                        return <GridCell key={`similar_${k}`}></GridCell>
                                    }

                                    const c = row.split(';')
                                    const src = `${URL.laravelBase}get-source-image/${projectId}/${c[0]}`
                                    return <GridCell key={`similar_${k}`}>
                                        <div className="centering">
                                            <a href={src} target="_blank"><img src={src} className={'max-image-size'} /></a>
                                        </div>
                                        <div className="centering">{c[1]}</div>
                                    </GridCell>
                                })
                                }
                            </SimilarImagesGrid>
                        }

                        {(exportSimilarity.isError && exportSimilarity.data?.error) &&
                            <Message
                                title={exportSimilarity.data?.error}
                                type="error"
                                icon={"exclamation-triangle"} >
                                {exportSimilarity.data?.error}
                            </Message>
                        }

                        <StyledGrid cols="auto">
                            <GridCell><FieldLabel>{trans('fill_similarity_title')}</FieldLabel></GridCell>
                            <GridCell>
                                <Toggler
                                    big={true}
                                    boolean={true}
                                    name={`settingsForm.similarity_ids_element`}
                                    onLabel={trans('fill_similarity_ids_element')}
                                    title={trans('fill_similarity_ids_element')}
                                    onChange={(e) => this.saveProjectSettings({ ...settingsForm, similarity_ids_element: e ? 1 : 0 })}
                                />
                            </GridCell>
                            <GridCell>
                                <Toggler
                                    big={true}
                                    boolean={true}
                                    name={`settingsForm.similarity_image_urls_element`}
                                    onLabel={trans('fill_similarity_image_urls_element')}
                                    title={trans('fill_similarity_image_urls_element')}
                                    onChange={(e) => this.saveProjectSettings({ ...settingsForm, similarity_image_urls_element: e ? 1 : 0 })}
                                />
                            </GridCell>
                        </StyledGrid>
                    </div>
                }
            </div>
        )
    }
}

const StyledGrid = css(Grid)({
    gridRowGap: '10px',
})

const StyledDiv = css('div')({
    /* ' .m-filterinput input': {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        borderBottom: '1px solid rgb(228,228,231)',
    }, */
    ' .no-items': {
        border: '1px solid rgb(228, 228, 231)',
        borderTopLeftRadius: '4px',
        borderTopRightRadius: '4px',
        padding: '5px 10px',
        marginBottom: '10px',
    }
})

const StyledInputGroup = css(InputGroup)({
    maxHeight: '200px',
    overflowY: 'auto',
    ' .muk-form-group': {
        marginBottom: 0,
    },
    ' .template-name': {
        background: 'transparent',
    },
    ' .sub': {
        paddingLeft: '10px',
        fontWeight: 'bold',
        fontSize: 'small',
        color: Theme.steelblue,
    },
    ' .products-count': {
        paddingLeft: '10px',
        fontSize: '11px',
        color: Theme.blue,
    },
    ' .inactive': {
        textDecoration: 'line-through',
        color: '#cccccc',
    },
})

const SimilarImagesGrid = css(Grid)({
    gridTemplateColumns: 'repeat(auto-fit, minmax(70px, 1fr))',
    ' .muk-gridcell': {
        textAlign: 'center',
        alignSelf: 'center',
    },
    ' img.max-image-size': {
        maxWidth: '80px',
        maxHeight: '80px',
    }
})

const CountDown = css("span")({
    padding: '10px',
    overflow: "hidden",
    WebkitAnimation: 'blink-animation .5s steps(4, start) infinite',
    animation: 'blink-animation .5s steps(4, start) infinite',
    "@keyframes blink-animation": {
        "to": { visibility: 'hidden' },
    },
})


function mapStateToProps(state) {
    return {
        settingsForm: selectFormValues(state, 'settingsForm'),
        exportSimilarity: state.exportSimilarity,
        templates: state.templates,
        queries: state.queries,
    }
}

export default connect(mapStateToProps, {
    createSimilarityExport,
})(VisualSimilarity)
